import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Container, Row, Col, Card, CardBody, Button } from "reactstrap";
import { Breadcrumbs, P } from "../../../AbstractElements";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import customers, {
  getAllCustomers,
  setCustomer,
} from "../../../store/slices/customers";
import { useNavigate } from "react-router";
import CreateUserModal from "./components/createUserModal";
// import UpdateUserModal from "./components/updateUserModal";
import GetButtons from "../../../Utils/getButtons";
import { Box } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { deleteUserThunk, getAllUsers } from "../../../store/slices/usersSlice";
import moment from "moment";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { useDebounce } from "../../../Hooks/useDebounce";

const UsersTable = () => {
  const history = useNavigate();
  const id = window.location.pathname.split("/").pop();
  const layout = id;
  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const [totalCount, setCount] = useState(0);
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isUpdateModalOpen, setUpdateModalOpen] = useState(false);
  // const [pagination, setPagination] = useState({
  //   pageIndex: 0,
  //   pageSize: 5,
  // });
  const customerData = useSelector((state) => state.customers.customers);
  const permissions = useSelector((state) => state.auth.permissions);
  const userRole = useSelector((state) => state.auth.user.role);
  const token = useSelector((state) => state.auth.token);

  const canExportUsers =
    userRole === "Admin" || permissions?.includes("users:export");
  const canCreateUsers =
    userRole === "Admin" || permissions?.includes("users:create");
  const canEditUsers =
    userRole === "Admin" || permissions?.includes("users:edit");
  const canDeleteUsers =
    userRole === "Admin" || permissions?.includes("users:delete");

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const columnFilterSearch = useDebounce(columnFilters, 300);

  const fetchData = async (payload) => {
    const sortField = sorting.length > 0 ? sorting[0].id : undefined;
    const sortOrder =
      sorting.length > 0 ? (sorting[0].desc ? "desc" : "asc") : undefined;

    const response = await dispatch(
      getAllUsers({
        pagination: true,
        limit: pageSize,
        offset: page * pageSize,
        sortBy: sortField,
        sortOrder: sortOrder,
        ...payload,
      }),
    );
    console.log("getAllUsers response:", response); // Log the response to see its structure
    if (response?.payload?.usersData && response?.payload?.count) {
      setData(response.payload.usersData || []);
      setCount(response.payload.count);
    } // Ensure the data is correctly set
  };

  useEffect(() => {
    fetchData({ searchQuery: debouncedSearch, filters: columnFilterSearch });
  }, [dispatch, page, pageSize, debouncedSearch, columnFilterSearch, sorting]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "ID",
        header: "ID",
        size: 150,
      },
      {
        accessorKey: "emp_id",
        header: "Employee Id",
        size: 150,
      },
      {
        accessorKey: "role.role_key",
        header: "Role",
        size: 150,
      },
      {
        accessorKey: "first_name",
        header: "First Name",
        size: 150,
      },
      {
        accessorKey: "last_name",
        header: "Last Name",
        size: 150,
      },
      {
        accessorKey: "full_name",
        header: "Full Name",
        size: 150,
      },
      {
        accessorKey: "email",
        header: "Email",
        size: 150,
      },
      {
        accessorKey: "mobile",
        header: "Mobile",
        size: 150,
      },
      {
        accessorKey: "username",
        header: "User Name",
        size: 150,
      },
      {
        accessorKey: "profile_img_id",
        header: "Profile Image Id",
        size: 150,
      },
      {
        accessorKey: "birth_date",
        header: "DOB",
        size: 150,
        Cell: ({ row }) => {
          return moment(row.birth_date).format("Do  MMM YYYY");
        },
      },
      {
        accessorKey: "action",
        header: "Actions",
        size: 150,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <div>
            <Button
              outline
              color="primary"
              onClick={(event) => {
                event.stopPropagation();
                rowAction("EDIT", row);
              }}
            >
              <FontAwesomeIcon data-action-type="edit" icon={faEdit} />
            </Button>

            <Button
              outline
              color="danger"
              onClick={(event) => {
                if (canDeleteUsers) rowAction("DELETE", row);
              }}
            >
              <FontAwesomeIcon data-action-type="delete" icon={faTrash} />
            </Button>
          </div>
        ),
      },
    ],
    [],
  );

  const deleteUserPermanent = async (userId) => {
    try {
      const resultAction = await dispatch(
        deleteUserThunk({ id: userId, token }),
      );

      if (deleteUserThunk.fulfilled.match(resultAction)) {
        toast.success("User deleted successfully!");
        fetchData();
        // ✅ Success feedback
        Swal.fire({
          title: "Deleted!",
          text: "The user has been deleted successfully.",
          icon: "success",
          confirmButtonColor: "#3085d6",
        });
      } else {
        // If rejectWithValue was used, the message will be in payload
        const msg =
          typeof resultAction.payload === "string"
            ? resultAction.payload
            : resultAction.payload?.message ||
              resultAction.error?.message ||
              "Failed to delete user. Please try again.";

        toast.error(msg);
        console.error("Delete user failed:", resultAction);
      }
    } catch (err) {
      console.error("Unexpected error deleting user:", err);
      toast.error("Unexpected error occurred while deleting user.");
    }
  };

  const rowAction = (actionType, row) => {
    console.log("rowdata", row);
    if (actionType === "EDIT") {
      setSelectedUser(row.original);
      setModalOpen(true);
      // setUpdateModalOpen(true);
      return;
    } else if (actionType === "DELETE") {
      Swal.fire({
        title: "Are you sure?",
        text: "This action will permanently delete the user.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33", // red for destructive
        cancelButtonColor: "#3085d6", // blue for cancel
        confirmButtonText: "Yes, delete it!",
      }).then((result) => {
        if (result.isConfirmed) {
          deleteUserPermanent(row?.original?.ID); // 🔥 call your function here
        }
      });
      return;
    } else {
      dispatch(setCustomer(row.original));
    }
  };

  /**
     Update that page and pageSize state based on the event received from the table pagination change.
     This function checks if the event is a function (which is the case when using functional updates)
     or a direct object. It then updates the page and pageSize state accordingly.
   */
  const handlePageChange = (event) => {
    const newState =
      typeof event === "function"
        ? event({ pageIndex: page, pageSize: pageSize })
        : event;

    setPage(newState.pageIndex);
    setPageSize(newState.pageSize);
  };

  const handleOnChangeSearch = (value) => {
    setSearchQuery(value);
    setPage(0);
  };

  const table = useMaterialReactTable({
    columns,
    data,
    globalFilter: searchQuery,
    onGlobalFilterChange: handleOnChangeSearch,
    initialState: { density: "compact" },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (event) => rowAction(event, row),
      sx: {
        cursor: "pointer",
      },
    }),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    rowCount: totalCount,
    onPaginationChange: (event) => handlePageChange(event), //hoist pagination state to your state when it changes internally
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    state: {
      pagination: { pageIndex: page, pageSize: pageSize },
      sorting,
      columnFilters,
    },

    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        sx={{ display: "flex", gap: "0.3rem", p: "0.5rem", flexWrap: "wrap" }}
      >
        {/* <GetButtons onClick={() => setModalOpen(true)} type="add" /> */}
        <GetButtons onClick={null} type="refresh" />
        <GetButtons columns={null} type="export" disabled={!canExportUsers} />
        <GetButtons
          onClick={() => {
            setModalOpen(true);
            setSelectedUser(null);
          }}
          type="add"
          disabled={!canCreateUsers}
        />
      </Box>
    ),
  });

  const toggle = useCallback(() => {
    setModalOpen(!isModalOpen);
  }, [isModalOpen]);

  return (
    <Fragment>
      {/*
        Model will render when the state is true 
        AND
        This component use for create and update both
      ***/}
      {isModalOpen && (
        <CreateUserModal
          isOpen={isModalOpen}
          toggle={toggle}
          selectedUser={selectedUser}
          fetchUser={fetchData}
          // pagination={{ pageIndex: page, pageSize: pageSize }}
        ></CreateUserModal>
      )}

      {/* <UpdateUserModal
        isOpen={isUpdateModalOpen}
        toggle={() => setUpdateModalOpen(false)}
        selectedUser={selectedUser}
      ></UpdateUserModal> */}

      <Breadcrumbs
        mainTitle="Users"
        parent="Users"
        parentPath="users"
        title="Users List"
      />
      <Container fluid={true}>
        <Row>
          <Col sm="12">
            <Card>
              <MaterialReactTable table={table} />
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default UsersTable;
